import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { AuthContext } from "../context/AuthContext";
import { getKumesler, postBakiciAtama, getBakiciInfo } from "../services/api";

const orangeColor = "#FF7A2B";
const orangeHoverColor = "#F57C00";

const BakiciAtamaModal = ({
  open,
  onClose,
  bakici,
  atananKumesler = [], // ← Bu prop BakicilarimPage'den geldi
  onSuccess,
}) => {
  const { token } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();

  const [kumesler, setKumesler] = useState([]);
  const [selectedKumesler, setSelectedKumesler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ureticiId, setUreticiId] = useState(null);

  const fetchUreticiId = async () => {
    try {
      const response = await getBakiciInfo();
      const bakiciBilgisi = Array.isArray(response.data?.data)
        ? response.data.data[0]
        : response.data?.data || response.data;
      const fetchedUreticiId = bakiciBilgisi?.URETICI_ID || null;
      setUreticiId(fetchedUreticiId);
    } catch (error) {
      //console.error("Üretici ID alınırken hata oluştu:", error);
      return null;
    }
  };

  const fetchKumesler = async () => {
    setLoading(true);
    try {
      const response = await getKumesler();
      const kumesData = response.data?.data || response.data || [];
      setKumesler(Array.isArray(kumesData) ? kumesData : []);
    } catch (error) {
      //console.error("kumes listesi yükleme hatası", error);
      enqueueSnackbar("kümes listesi yüklenemedi", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUreticiId();
      fetchKumesler();
      setSelectedKumesler([]); // Seçimleri temizle
    }
  }, [open]);

  // Kümes seçimi değiştiğinde
  const handleKumesChange = (event) => {
    setSelectedKumesler(event.target.value);
  };

  // Atama işlemini gerçekleştir
  const handleSubmit = async () => {
    if (selectedKumesler.length === 0) {
      enqueueSnackbar("Lütfen en az bir kümes seçin", { variant: "warning" });
      return;
    }

    if (!ureticiId) {
      enqueueSnackbar("Üretici bilgisi alınamadı", { variant: "error" });
      return;
    }

    setLoading(true);
    try {
      // Her kümes için ayrı atama isteği gönder
      const promises = selectedKumesler.map((kumesId) =>
        postBakiciAtama({
          URETICI_ID: ureticiId,
          KUMES_ID: kumesId,
          BAKICI_KULLANICI_ID: bakici.BAKICI_KULLANICI_ID,
        })
      );

      await Promise.all(promises);

      enqueueSnackbar(
        `Bakıcı ${selectedKumesler.length} kümese başarıyla atandı!`,
        { variant: "success" }
      );
      onSuccess();
      onClose();
    } catch (error) {
      //console.error("Bakıcı atama hatası:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.data ||
        error.message ||
        "Bakıcı atama sırasında hata oluştu";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Kümesin zaten atanmış olup olmadığını kontrol et
  const isKumesAlreadyAssigned = (kumesId) => {
    return atananKumesler.some(
      (atanmisKumes) =>
        atanmisKumes.KUMES_ID === kumesId || atanmisKumes.ID === kumesId
    );
  };

  // ✅ Kümes seçim handler'ını güncelle
  const handleKumesSecimDegistir = (kumesId, secili) => {
    // Zaten atanmışsa seçime izin verme
    if (isKumesAlreadyAssigned(kumesId)) {
      enqueueSnackbar("Bu kümes zaten atanmış", { variant: "warning" });
      return;
    }

    if (secili) {
      setSelectedKumesler((prev) => [...prev, kumesId]);
    } else {
      setSelectedKumesler((prev) => prev.filter((id) => id !== kumesId));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: orangeColor, fontWeight: "bold" }}>
        Bakıcı Atama - {bakici?.BAKICI_ADI} {bakici?.BAKICI_SOYADI}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress sx={{ color: orangeColor }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Seç</TableCell>
                    <TableCell>Kümes Adı</TableCell>
                    <TableCell>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kumesler.map((kumes) => {
                    const kumesId = kumes.KUMES_ID;
                    const isSelected = selectedKumesler.includes(kumesId);
                    const isAlreadyAssigned = isKumesAlreadyAssigned(kumesId);

                    return (
                      <TableRow
                        key={kumesId}
                        sx={{
                          backgroundColor: isAlreadyAssigned
                            ? "#f0f8f0"
                            : "inherit", // ← Atanmışlar yeşil
                          opacity: isAlreadyAssigned ? 0.7 : 1, // ← Atanmışlar soluk
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) =>
                              handleKumesSecimDegistir(
                                kumesId,
                                e.target.checked
                              )
                            }
                            disabled={isAlreadyAssigned} // ✅ Atanmışlar devre dışı
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {kumes.KUMES_ADI}
                            {/* ✅ Atanmış işareti */}
                            {isAlreadyAssigned && (
                              <Chip
                                label="Atanmış"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {isAlreadyAssigned ? (
                            <Typography variant="caption" color="success.main">
                              ✓ Zaten atanmış
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Atanabilir
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ✅ Modal başlığına bilgilendirme ekleyin */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                ℹ️ Yeşil renkli kümeler zaten bu bakıcıya atanmış durumdadır ve
                seçilemez.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={selectedKumesler.length === 0 || loading}
          sx={{
            backgroundColor: "#FF7A2B",
            "&:hover": { backgroundColor: orangeHoverColor },
          }}
        >
          {loading ? "Atanıyor..." : "Ata"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BakiciAtamaModal;
